/* Pantheratec desktop navigation 1.2. No libraries, no API calls, no global mobile selectors. */
(() => {
  'use strict';
  if (customElements.get('pt-desktop-nav')) return;

  class PTDesktopNav extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this._connected) return;
        this._connected = true;
        this.init();
      });
    }

    init() {
      this.header = this.closest('header');
      this.section = this.closest('.shopify-section') || this.header?.parentElement;
      this.probe = this.section?.querySelector('[data-pt-desktop-probe]') || document.querySelector('[data-pt-desktop-probe]');
      // If the theme renders a detached sticky copy, use the same responsive
      // utility directly on that copy instead of abandoning its navigation.
      if (!this.probe) {
        this.probe = document.createElement('span');
        this.probe.className = 'hidden lg:flex';
        this.probe.setAttribute('aria-hidden', 'true');
        this.probe.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;';
        this.append(this.probe);
      }
      this.row = this.querySelector('[data-pt-row]');
      this.more = this.querySelector('[data-pt-more]');
      this.moreButton = this.querySelector('[data-pt-more-toggle]');
      this.overflowPanel = this.querySelector('[data-pt-overflow-panel]');
      this.overflowList = this.querySelector('[data-pt-overflow]');
      if (!this.header || !this.row || !this.more || !this.probe) return;
      this.items = Array.from(this.querySelectorAll('[data-pt-item]'))
        .sort((a, b) => Number(a.dataset.ptKey) - Number(b.dataset.ptKey));
      this.lastLayout = undefined;
      this.panels = Array.from(this.querySelectorAll('[data-pt-panel-content]'));
      this.openPanel = null;
      this.openItem = null;
      this.fromMore = false;
      this.moreOpen = false;
      this.desktop = false;
      this.hoverEnabled = this.dataset.hover === 'true';
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.tagHeaderSlots();
      this.createMeasurer();
      this.addEventListener('click', event => this.onClick(event), { signal: this.signal });
      this.addEventListener('keydown', event => this.onKeydown(event), { signal: this.signal });
      this.addEventListener('pointerover', event => this.onPointerOver(event), { signal: this.signal });
      this.addEventListener('pointerout', event => this.onPointerOut(event), { signal: this.signal });
      this.addEventListener('focusout', () => {
        setTimeout(() => {
          if (this.isConnected && !this.contains(document.activeElement)) this.closeAll();
        }, 0);
      }, { signal: this.signal });
      document.addEventListener('pointerdown', event => {
        if (this.desktop && !this.contains(event.target)) this.closeAll();
      }, { signal: this.signal });
      document.addEventListener('shopify:block:select', event => {
        if (!this.desktop) return;
        const item = this.items.find(el => el.dataset.ptBlock === String(event.detail?.blockId));
        if (item?.dataset.ptPanel) this.showPanel(item, true);
      }, { signal: this.signal });
      document.addEventListener('shopify:block:deselect', () => this.closeAll(), { signal: this.signal });
      window.addEventListener('resize', () => this.scheduleLayout(), { passive: true, signal: this.signal });
      window.addEventListener('scroll', () => {
        if (!this.desktop || (!this.openPanel && !this.moreOpen)) return;
        this.positionPanels();
        const rect = this.header.getBoundingClientRect();
        if (rect.bottom < 8 || rect.top >= window.innerHeight) this.closeAll();
      }, { passive: true, signal: this.signal });
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.scheduleLayout());
        this.observer.observe(this);
        this.observer.observe(this.header);
        this.observer.observe(this.measure);
      }
      if (document.fonts) {
        document.fonts.ready.then(() => { if (this.isConnected) this.scheduleLayout(); });
        document.fonts.addEventListener?.('loadingdone', () => this.scheduleLayout(), { signal: this.signal });
      }
      this.scheduleLayout();
    }

    tagHeaderSlots() {
      // Comment boundaries preserve the native mobile DOM, without adding wrappers.
      let slot = null;
      const slots = new Map();
      for (const node of this.header.childNodes) {
        if (node.nodeType === Node.COMMENT_NODE) {
          const text = node.nodeValue.trim();
          if (text.startsWith('pt-slot:')) slot = text.slice(8);
          else if (text === '/pt-slot') slot = null;
        } else if (slot && node.nodeType === Node.ELEMENT_NODE && !node.matches('script,style,link,template')) {
          if (!slots.has(slot)) slots.set(slot, []);
          slots.get(slot).push(node);
        }
      }
      for (const [name, nodes] of slots) {
        const wrapper = nodes.find(node => node.matches('.header__icons,.header__logo,.header__heading')) || nodes[0];
        wrapper?.setAttribute('data-pt-slot', name);
      }
    }

    createMeasurer() {
      this.measure = document.createElement('div');
      this.measure.className = 'pt-nav__measure';
      this.measure.setAttribute('aria-hidden', 'true');
      this.measure.inert = true;
      const list = document.createElement('ul');
      this.measureItems = this.items.map(item => {
        const copy = item.cloneNode(true);
        copy.removeAttribute('data-pt-item');
        copy.removeAttribute('data-pt-key');
        copy.removeAttribute('data-pt-pin');
        copy.removeAttribute('data-pt-block');
        copy.removeAttribute('data-shopify-editor-block');
        [copy, ...copy.querySelectorAll('*')].forEach(el => {
          el.removeAttribute('id');
          el.removeAttribute('aria-controls');
          el.removeAttribute('data-pt-open');
          el.removeAttribute('data-pt-panel');
          if (el.matches('a, button')) el.tabIndex = -1;
        });
        list.append(copy);
        return copy;
      });
      const moreCopy = document.createElement('li');
      moreCopy.className = 'pt-nav__more';
      this.measureMore = this.moreButton.cloneNode(true);
      this.measureMore.removeAttribute('aria-controls');
      this.measureMore.removeAttribute('data-pt-more-toggle');
      this.measureMore.tabIndex = -1;
      moreCopy.append(this.measureMore);
      list.append(moreCopy);
      this.measure.append(list);
      this.append(this.measure);
    }

    scheduleLayout() {
      if (!this.isConnected || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.layout();
      });
    }

    layout() {
      // Same responsive breakpoint as the original theme, not an assumed pixel value.
      const desktop = getComputedStyle(this.probe).display !== 'none';
      if (!desktop) {
        this.desktop = false;
        this.closeAll();
        this.header.removeAttribute('data-pt-desktop');
        return;
      }
      this.desktop = true;
      this.header.dataset.ptDesktop = 'true';
      this.dataset.ptReady = 'true';
      const available = this.row.getBoundingClientRect().width;
      if (available <= 0) return;
      const gap = parseFloat(getComputedStyle(this.row).columnGap) || 0;
      const widths = this.measureItems.map(el => el.getBoundingClientRect().width);
      const moreWidth = this.measureMore.getBoundingClientRect().width;
      let visible = this.items.map((_, index) => index);
      const total = indexes => indexes.reduce((sum, index) => sum + widths[index], 0);
      if (total(visible) + gap * Math.max(0, visible.length - 1) > available - 1) {
        while (visible.length && total(visible) + moreWidth + gap * visible.length > available - 1) {
          let removeAt = -1;
          for (let i = visible.length - 1; i >= 0; i--) {
            if (this.items[visible[i]].dataset.ptPin !== 'true') { removeAt = i; break; }
          }
          if (removeAt === -1) removeAt = visible.length - 1;
          visible.splice(removeAt, 1);
        }
      }
      const visibleSet = new Set(visible);
      const overflow = this.items.map((_, index) => index).filter(index => !visibleSet.has(index));
      const signature = visible.join(',');
      if (signature !== this.lastLayout) {
        const focused = this.contains(document.activeElement) ? document.activeElement : null;
        const focusedItem = focused?.closest('[data-pt-item]');
        this.closeAll();
        const frag = document.createDocumentFragment();
        let placedMore = false;
        for (const index of visible) {
          const item = this.items[index];
          if (overflow.length && !placedMore && item.dataset.ptPin === 'true') {
            frag.append(this.more);
            placedMore = true;
          }
          frag.append(item);
        }
        if (!placedMore) frag.append(this.more);
        this.row.append(frag);
        for (const index of overflow) this.overflowList.append(this.items[index]);
        this.more.hidden = overflow.length === 0;
        this.lastLayout = signature;
        if (focused && focusedItem) {
          const inOverflow = focusedItem.parentElement === this.overflowList;
          (inOverflow ? this.moreButton : focused).focus({ preventScroll: true });
        }
      }
      this.positionPanels();
    }

    positionPanels() {
      const rect = this.header.getBoundingClientRect();
      const height = Math.max(120, window.innerHeight - Math.max(0, rect.bottom) - 16);
      this.style.setProperty('--pt-panel-height', `${Math.round(height)}px`);
      if (!this.more.hidden) {
        const button = this.moreButton.getBoundingClientRect();
        const width = Math.min(400, window.innerWidth - 40);
        const left = Math.max(12, Math.min(button.right - rect.left - width, rect.width - width - 12));
        this.style.setProperty('--pt-overflow-left', `${Math.round(left)}px`);
        this.style.setProperty('--pt-overflow-right', 'auto');
      }
    }

    clearTimers() { clearTimeout(this.hoverTimer); clearTimeout(this.closeTimer); }

    closePanels() {
      for (const panel of this.panels) panel.hidden = true;
      for (const item of this.items) {
        item.classList.remove('is-open');
        item.querySelector('[data-pt-open]')?.setAttribute('aria-expanded', 'false');
      }
      this.openPanel = null;
      this.openItem = null;
      this.more.classList.remove('is-open');
      this.removeAttribute('data-pt-open-state');
    }

    closeMore() {
      this.moreOpen = false;
      this.overflowPanel.hidden = true;
      this.moreButton.setAttribute('aria-expanded', 'false');
      this.more.classList.remove('is-open');
    }

    closeAll(restoreFocus = false) {
      this.clearTimers();
      const returnTarget = this.openItem && !this.fromMore
        ? this.openItem.querySelector('[data-pt-open]')
        : this.moreButton;
      const hadOpen = this.openPanel || this.moreOpen;
      this.closePanels();
      this.closeMore();
      this.fromMore = false;
      if (restoreFocus && hadOpen && returnTarget && !returnTarget.closest('[hidden]')) {
        returnTarget.focus({ preventScroll: true });
      }
    }

    showMore(focusFirst = false) {
      if (!this.desktop || this.more.hidden) return;
      this.clearTimers();
      this.closePanels();
      this.fromMore = false;
      this.moreOpen = true;
      this.overflowPanel.hidden = false;
      this.moreButton.setAttribute('aria-expanded', 'true');
      this.more.classList.add('is-open');
      this.positionPanels();
      if (focusFirst) this.overflowList.querySelector('a,button')?.focus({ preventScroll: true });
    }

    showPanel(item, focus = false) {
      if (!this.desktop || !item?.dataset.ptPanel) return;
      const panel = this.panels.find(el => el.id === item.dataset.ptPanel);
      if (!panel) return;
      this.clearTimers();
      const fromMore = item.parentElement === this.overflowList;
      this.closePanels();
      this.closeMore();
      this.fromMore = fromMore;
      this.openPanel = panel;
      this.openItem = item;
      panel.hidden = false;
      panel.querySelector('[data-pt-back]').hidden = !fromMore;
      item.classList.add('is-open');
      item.querySelector('[data-pt-open]')?.setAttribute('aria-expanded', 'true');
      this.more.classList.toggle('is-open', fromMore);
      this.dataset.ptOpenState = 'panel';
      this.positionPanels();
      if (focus) this.focusPanel(panel);
    }

    focusPanel(panel) {
      const target = this.fromMore ? panel.querySelector('[data-pt-back]') : panel.querySelector('.pt-nav__all');
      target?.focus({ preventScroll: true });
    }

    onClick(event) {
      if (!this.desktop || this.measure.contains(event.target)) return;
      if (event.target.closest('[data-pt-more-toggle]')) {
        event.preventDefault();
        if (this.moreOpen) this.closeAll(); else this.showMore();
        return;
      }
      const toggle = event.target.closest('[data-pt-open]');
      if (toggle) {
        event.preventDefault();
        const item = toggle.closest('[data-pt-item]');
        if (this.openItem === item) this.closeAll(true); else this.showPanel(item, true);
        return;
      }
      if (event.target.closest('[data-pt-back]')) {
        event.preventDefault();
        const previous = this.openItem;
        this.showMore();
        (previous?.querySelector('[data-pt-open]') || this.moreButton).focus({ preventScroll: true });
        return;
      }
      if (event.target.closest('[data-pt-close]')) {
        event.preventDefault();
        this.closeAll(true);
      }
    }

    onKeydown(event) {
      if (!this.desktop || this.measure.contains(event.target)) return;
      if (event.key === 'Escape' && (this.openPanel || this.moreOpen)) {
        event.preventDefault();
        event.stopPropagation();
        this.closeAll(true);
        return;
      }
      const item = event.target.closest('[data-pt-item]');
      if (event.key === 'ArrowDown') {
        if (event.target.closest('[data-pt-more-toggle]')) {
          event.preventDefault();
          this.showMore(true);
        } else if (item?.dataset.ptPanel && (item.parentElement === this.row || item.parentElement === this.overflowList)) {
          event.preventDefault();
          this.showPanel(item, true);
        }
      }
      // Panels are outside the row for stable positioning; keep Tab order intuitive.
      if (event.key === 'Tab' && this.openPanel) {
        const toggle = this.openItem?.querySelector('[data-pt-open]');
        const focusables = Array.from(this.openPanel.querySelectorAll('a[href],button:not([disabled])'))
          .filter(el => !el.closest('[hidden]'));
        if (!event.shiftKey && event.target === toggle) {
          event.preventDefault();
          focusables[0]?.focus();
        } else if (event.shiftKey && event.target === focusables[0]) {
          event.preventDefault();
          const target = this.fromMore ? this.moreButton : toggle;
          this.closeAll();
          target?.focus();
        } else if (!event.shiftKey && event.target === focusables[focusables.length - 1]) {
          const origin = this.fromMore ? this.moreButton : toggle;
          this.closeAll();
          // Close first so hidden panel links cannot be included in the document tab order.
          const all = Array.from(document.querySelectorAll('a[href],button,input,select,textarea,[tabindex="0"]'))
            .filter(el => !el.closest('[hidden],[inert]') && el.tabIndex >= 0 && el.getClientRects().length);
          const index = all.indexOf(origin);
          if (index !== -1 && all[index + 1]) {
            event.preventDefault();
            all[index + 1].focus();
          }
        }
      }
    }

    onPointerOver(event) {
      if (!this.desktop || !this.hoverEnabled || event.pointerType !== 'mouse' || this.measure.contains(event.target)) return;
      clearTimeout(this.closeTimer);
      if (event.target.closest('[data-pt-panel-content], [data-pt-overflow-panel]')) {
        clearTimeout(this.hoverTimer);
        return;
      }
      const item = event.target.closest('[data-pt-item]');
      const more = event.target.closest('[data-pt-more]');
      // Overflow items must open their full mega panels on hover as well.
      if (item && (item.parentElement === this.row || item.parentElement === this.overflowList)) {
        if (item.contains(event.relatedTarget)) return;
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(() => {
          if (item.dataset.ptPanel) this.showPanel(item); else this.closeAll();
        }, 140);
      } else if (more && !more.contains(event.relatedTarget)) {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(() => this.showMore(), 140);
      }
    }

    onPointerOut(event) {
      if (!this.desktop || !this.hoverEnabled || event.pointerType !== 'mouse') return;
      if (!this.contains(event.relatedTarget)) {
        clearTimeout(this.hoverTimer);
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => {
          if (!this.contains(document.activeElement)) this.closeAll();
        }, 220);
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.clearTimers();
      this.measure?.remove();
      this.header?.removeAttribute('data-pt-desktop');
      this._connected = false;
    }
  }
  customElements.define('pt-desktop-nav', PTDesktopNav);
})();


/* v1.4: Recover Concept's scroll-up reveal only on collection pages.
   No fixed positioning; no global transform:none. The theme owns hiding. */
(() => {
  'use strict';
  if (window.__ptCollectionHeaderRevealV14) return;
  window.__ptCollectionHeaderRevealV14 = true;

  function initCollectionReveal() {
    if (!document.querySelector('[data-ptc-collection]')) return;
    const header = document.querySelector(
      'header.pt-custom-header[is="sticky-header"][data-sticky-type="on-scroll-up"]'
    );
    if (!header || header.dataset.ptCollectionRevealBound === 'true') return;
    header.dataset.ptCollectionRevealBound = 'true';

    const probe = document.querySelector('[data-pt-desktop-probe]');
    const desktopEnabled = () => probe && getComputedStyle(probe).display !== 'none';
    let lastDirectionY = Math.max(0, window.scrollY);
    let scheduled = false;

    function show() { header.dataset.ptCollectionReveal = 'true'; }
    function releaseToTheme() { header.removeAttribute('data-pt-collection-reveal'); }

    function synchronize(initial = false) {
      const y = Math.max(0, window.scrollY);
      if (!desktopEnabled()) {
        releaseToTheme();
        lastDirectionY = y;
        return;
      }

      // Always reset the stale negative translate when back at page top.
      if (y <= 5) {
        show();
        lastDirectionY = y;
        return;
      }

      // At the beginning of the page the header is in normal view.
      if (initial) {
        if (y < header.offsetHeight + 15) show();
        else releaseToTheme();
        lastDirectionY = y;
        return;
      }

      // Ignore subpixel/jitter movements but accumulate their distance.
      const movement = y - lastDirectionY;
      if (Math.abs(movement) < 5) return;
      lastDirectionY = y;
      if (y <= header.offsetHeight + 15) {
        show();
      } else if (movement < 0) {
        show();
      } else {
        // As soon as the user scrolls down, hand transforms back to Concept.
        releaseToTheme();
      }
    }

    function onScroll() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        if (header.isConnected) synchronize();
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => synchronize(true), { passive: true });
    // Set initial state after the theme's sticky-header component starts.
    requestAnimationFrame(() => synchronize(true));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionReveal, { once: true });
  } else {
    initCollectionReveal();
  }
  document.addEventListener('shopify:section:load', initCollectionReveal);
})();


/* Pantheratec v2.0 - search layering on collection pages only.
 * Concept renders Search Drawer in the Overlay group, outside the header.
 * Releasing the elevated header while it is open prevents the navigation
 * from covering the search input. Never changes the native mobile header.
 */
(() => {
  'use strict';
  if (window.__ptCollectionSearchLayerV20) return;
  window.__ptCollectionSearchLayerV20 = true;

  const root = document.documentElement;
  const className = 'pt-search-overlay-active';
  const searchSelector = [
    '#SearchDrawer', '#search-drawer',
    'search-drawer', '[is="search-drawer"]',
    '[data-search-drawer]', '.search-drawer', '.search-modal',
    '[role="dialog"][aria-label*="Suche"]',
    '[role="dialog"][aria-label*="Search"]'
  ].join(',');

  let monitor = null;
  let seenOpen = false;
  let monitorTicks = 0;

  function isCollectionDesktop() {
    if (!document.querySelector('[data-ptc-collection]')) return false;
    const probe = document.querySelector('[data-pt-desktop-probe]');
    return Boolean(probe && getComputedStyle(probe).display !== 'none');
  }

  function isVisible(el) {
    if (el.closest('[hidden]') || el.getAttribute('aria-hidden') === 'true') return false;
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) <= 0.05) return false;
    const r = el.getBoundingClientRect();
    return r.width >= 140 && r.height >= 80 &&
      r.right > 8 && r.left < window.innerWidth - 8 &&
      r.bottom > 8 && r.top < window.innerHeight - 8 &&
      style.pointerEvents !== 'none';
  }

  function searchIsOpen() {
    const searchInput = document.activeElement;
    if (searchInput && searchInput.matches('input[type="search"]') && isVisible(searchInput)) return true;
    const drawers = document.querySelectorAll(searchSelector);
    return Array.from(drawers).some(el => {
      if (!isVisible(el)) return false;
      if (el.matches('dialog')) return el.open;
      return true;
    });
  }

  function stop() {
    root.classList.remove(className);
    seenOpen = false;
    monitorTicks = 0;
    if (monitor !== null) clearInterval(monitor);
    monitor = null;
  }

  function tick() {
    if (!isCollectionDesktop()) { stop(); return; }
    monitorTicks += 1;
    if (searchIsOpen()) {
      seenOpen = true;
      return;
    }
    // Wait for Concept's open animation; once an open panel has disappeared
    // the normal collection sticky header may return to its previous layer.
    if (seenOpen || monitorTicks > 12) stop();
  }

  function start() {
    if (!isCollectionDesktop()) return;
    root.classList.add(className);
    seenOpen = false;
    monitorTicks = 0;
    if (monitor !== null) clearInterval(monitor);
    monitor = setInterval(tick, 250);
  }

  function isSearchTrigger(element) {
    const clickable = element.closest('button, a, summary, [role="button"]');
    if (!clickable?.closest('header.pt-custom-header')) return false;
    const attrs = [
      clickable.getAttribute('aria-controls'), clickable.getAttribute('aria-label'),
      clickable.getAttribute('title'), clickable.getAttribute('href'),
      clickable.getAttribute('data-drawer'), clickable.getAttribute('data-target'),
      typeof clickable.className === 'string' ? clickable.className : ''
    ].filter(Boolean).join(' ').toLowerCase();
    return /search|suche/.test(attrs);
  }

  // Capture clicks on the existing search button without changing its action.
  document.addEventListener('click', event => {
    if (!isCollectionDesktop()) return;
    if (isSearchTrigger(event.target)) {
      if (root.classList.contains(className) && searchIsOpen()) {
        // Some search buttons toggle the drawer. Re-evaluate after Concept acts.
        setTimeout(tick, 280);
      } else {
        start();
      }
      return;
    }
    if (!root.classList.contains(className)) return;
    const closeControl = event.target.closest(
      '[aria-label*="Schließen"], [aria-label*="Close"], ' +
      '[data-drawer-close], [data-search-close], .drawer__overlay, .drawer__backdrop'
    );
    if (closeControl) setTimeout(tick, 300);
  }, true);

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && root.classList.contains(className)) {
      setTimeout(tick, 250);
    }
  }, true);
  window.addEventListener('resize', () => { if (!isCollectionDesktop()) stop(); }, { passive: true });
  document.addEventListener('shopify:section:unload', stop);
})();


/* ====================================================================
 * PANTHERATEC v2.1 - Conce pt minicart layering, collections only.
 * Complements the existing v2.0 SEARCH handling, independently.
 * All native cart triggers, dismissal, checkout and cart logic stay in
 * the original theme.  This code changes only a class on <html>.
 * ==================================================================== */
(() => {
  'use strict';
  if (window.__ptCollectionCartLayerV21) return;
  window.__ptCollectionCartLayerV21 = true;

  const root = document.documentElement;
  const activeClass = 'pt-cart-overlay-active';
  const cartSelector = [
    '#CartDrawer', '#cart-drawer', 'cart-drawer',
    '[is="cart-drawer"]', '[data-cart-drawer]', '.cart-drawer',
    '[id*="cart-drawer" i]', '[id*="cartdrawer" i]',
    'drawer-element[id*="cart" i]', '.drawer--cart',
    '[role="dialog"][aria-label*="Warenkorb" i]',
    '[role="dialog"][aria-label*="Einkaufswagen" i]',
    '[role="dialog"][aria-label*="Cart" i]'
  ].join(',');

  let timer = null;
  let observedOpen = false;
  let ticks = 0;

  function collectionDesktop() {
    if (!document.querySelector('[data-ptc-collection]')) return false;
    const probe = document.querySelector('[data-pt-desktop-probe]');
    return Boolean(probe && getComputedStyle(probe).display !== 'none');
  }

  function visuallyOpen(element) {
    if (element.matches('dialog') && !element.open) return false;
    if (element.matches('details') && !element.open) return false;
    if (element.getAttribute('aria-hidden') === 'true' || element.hasAttribute('hidden')) return false;
    let ancestor = element;
    while (ancestor && ancestor.nodeType === 1) {
      if (ancestor.hasAttribute('hidden') || ancestor.hasAttribute('inert') ||
          ancestor.getAttribute('aria-hidden') === 'true') return false;
      const style = getComputedStyle(ancestor);
      if (style.display === 'none' || style.visibility === 'hidden' ||
          Number(style.opacity) < 0.05) return false;
      ancestor = ancestor.parentElement;
    }
    const rect = element.getBoundingClientRect();
    return rect.width >= 160 && rect.height >= 140 &&
      rect.left < window.innerWidth - 24 && rect.right > 24 &&
      rect.top < window.innerHeight - 24 && rect.bottom > 24;
  }

  function cartOpen() {
    const items = document.querySelectorAll(cartSelector);
    for (const item of items) {
      if (visuallyOpen(item)) return true;
    }
    return false;
  }

  function stop() {
    root.classList.remove(activeClass);
    observedOpen = false;
    ticks = 0;
    if (timer !== null) window.clearInterval(timer);
    timer = null;
  }

  function check() {
    if (!collectionDesktop()) { stop(); return; }
    ticks++;
    if (cartOpen()) {
      observedOpen = true;
      // If the drawer was opened without clicking the header cart icon,
      // activating the class on detection still fixes its overlay order.
      root.classList.add(activeClass);
      return;
    }
    // Keep the lower header layer during the native opening animation;
    // restore its normal sticky/menu stacking immediately after closing.
    if (observedOpen || ticks > 24) stop();
  }

  function start() {
    if (!collectionDesktop()) return;
    root.classList.add(activeClass);
    observedOpen = false;
    ticks = 0;
    if (timer !== null) window.clearInterval(timer);
    timer = window.setInterval(check, 250);
    window.requestAnimationFrame(check);
  }

  function cartTrigger(target) {
    const clickable = target.closest('button, a, summary, [role="button"]');
    if (!clickable) return false;
    // Follow the real Concept header cart trigger; do not match unrelated
    // inline product or navigation links just because their URL says /cart.
    const insideHeader = Boolean(clickable.closest('header.pt-custom-header'));
    const controls = [
      clickable.getAttribute('aria-controls'),
      clickable.getAttribute('data-drawer'),
      clickable.getAttribute('data-target')
    ].filter(Boolean).join(' ').toLowerCase();
    if (/cart|warenkorb|einkaufswagen/.test(controls)) return true;
    if (!insideHeader) return false;
    const clues = [
      clickable.getAttribute('aria-label'), clickable.getAttribute('title'),
      clickable.getAttribute('href'), clickable.getAttribute('id'),
      typeof clickable.className === 'string' ? clickable.className : ''
    ].filter(Boolean).join(' ').toLowerCase();
    return /cart|warenkorb|einkaufswagen/.test(clues);
  }

  document.addEventListener('click', event => {
    if (!collectionDesktop()) return;
    if (cartTrigger(event.target)) {
      start();
      return;
    }
    if (!root.classList.contains(activeClass)) return;
    const close = event.target.closest(
      '[aria-label*="Schließen" i], [aria-label*="Close" i], ' +
      '[data-drawer-close], [data-cart-close], .drawer__overlay, .drawer__backdrop'
    );
    if (close) window.setTimeout(check, 400);
  }, true);

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && root.classList.contains(activeClass)) {
      window.setTimeout(check, 400);
    }
  }, true);
  document.addEventListener('cart:open', start);
  document.addEventListener('cart:opened', start);
  window.addEventListener('resize', () => {
    if (!collectionDesktop()) stop();
  }, { passive: true });
  document.addEventListener('shopify:section:unload', stop);
})();
